import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Bookmark, Download, FileUp, Mail, Plus, Search, SlidersHorizontal, Tag, Upload, Users, X,
} from "lucide-react";
import { toast } from "sonner";

import { API, api, apiError, fmtDate, initials } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader, StatusBadge, TableSkeleton } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SelectField, TagsField, TextAreaField, TextField } from "@/components/forms/Fields";
import CandidateForm from "@/components/forms/CandidateForm";
import ResumeUpload from "@/components/forms/ResumeUpload";

const PAGE_SIZE = 20;

function BulkBar({ ids, onDone, clear }) {
  const { meta } = useAuth();
  const [action, setAction] = useState(null);
  const [tags, setTags] = useState([]);
  const [value, setValue] = useState(null);
  const [confirmReject, setConfirmReject] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState({ subject: "", body: "", template_key: "candidate_outreach" });
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    api.get("/communications/templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const run = async (act, payload) => {
    try {
      const { data } = await api.post(`/candidates/bulk?action=${act}`, { candidate_ids: ids, ...payload });
      toast.success(`${data.updated} candidate${data.updated === 1 ? "" : "s"} updated`);
      setAction(null);
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const sendBulk = async () => {
    try {
      const { data } = await api.post("/communications/bulk-email", { candidate_ids: ids, ...email });
      toast.success(`Sent to ${data.sent} candidate(s)`);
      if (data.failed?.length) toast.warning(`${data.failed.length} could not be emailed`);
      setEmailOpen(false);
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const exportSelected = () => {
    window.open(`${API}/candidates/export/csv?ids=${ids.join(",")}`, "_blank");
  };

  return (
    <div className="surface flex flex-wrap items-center gap-2 rounded-md p-3" data-testid="bulk-action-bar">
      <span className="mr-1 text-xs text-slate-600">{ids.length} selected</span>
      {[
        ["tag", "Tag", Tag],
        ["assign", "Assign", Users],
        ["add_to_job", "Add to job", Plus],
        ["talent_pool", "Talent pool", Bookmark],
      ].map(([key, label, Icon]) => (
        <Button
          key={key}
          size="sm"
          variant="ghost"
          onClick={() => setAction(key)}
          data-testid={`bulk-${key}`}
          className="h-8 gap-1.5 rounded-full border border-slate-200 text-xs text-slate-600 hover:bg-slate-100"
        >
          <Icon className="h-3 w-3" /> {label}
        </Button>
      ))}
      <Button size="sm" variant="ghost" onClick={() => setEmailOpen(true)} data-testid="bulk-email"
        className="h-8 gap-1.5 rounded-full border border-slate-200 text-xs text-slate-600 hover:bg-slate-100">
        <Mail className="h-3 w-3" /> Email
      </Button>
      <Button size="sm" variant="ghost" onClick={exportSelected} data-testid="bulk-export"
        className="h-8 gap-1.5 rounded-full border border-slate-200 text-xs text-slate-600 hover:bg-slate-100">
        <Download className="h-3 w-3" /> Export
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setConfirmReject(true)} data-testid="bulk-reject"
        className="h-8 rounded-full border border-rose-200 text-xs text-rose-700 hover:bg-rose-50">
        Reject
      </Button>
      <Button size="sm" variant="ghost" onClick={clear} className="h-8 text-xs text-slate-500">
        <X className="h-3 w-3" />
      </Button>

      <Dialog open={!!action} onOpenChange={(v) => !v && setAction(null)}>
        <DialogContent className="max-w-md border-slate-200 bg-white">
          <DialogHeader>
            <DialogTitle className="text-base text-slate-900">
              {action === "tag" ? "Add tags" : action === "assign" ? "Assign recruiter"
                : action === "add_to_job" ? "Add to a job pipeline" : "Talent pool membership"}
            </DialogTitle>
          </DialogHeader>
          {action === "tag" && <TagsField label="Tags" values={tags} onChange={setTags} testid="bulk-tags-input" />}
          {action === "assign" && (
            <SelectField label="Recruiter" value={value} onChange={setValue} testid="bulk-assign-select"
              options={(meta?.users || []).map((u) => ({ value: u.id, label: u.name }))} />
          )}
          {action === "add_to_job" && (
            <SelectField label="Job" value={value} onChange={setValue} testid="bulk-job-select"
              options={(meta?.jobs || []).map((j) => ({ value: j.id, label: `${j.job_code} · ${j.title}` }))} />
          )}
          {action === "talent_pool" && (
            <SelectField label="Membership" value={value} onChange={setValue}
              options={[{ value: "yes", label: "Add to talent pool" }, { value: "no", label: "Remove from talent pool" }]} />
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAction(null)} className="text-slate-500">Cancel</Button>
            <Button
              data-testid="bulk-confirm"
              onClick={() =>
                action === "tag" ? run("tag", { tags })
                  : action === "assign" ? run("assign", { recruiter_id: value })
                  : action === "add_to_job" ? run("add_to_job", { job_id: value, stage: "New" })
                  : run("talent_pool", { in_talent_pool: value === "yes" })
              }
              className="rounded-full bg-blue-600 px-5 hover:bg-blue-500"
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-xl border-slate-200 bg-white">
          <DialogHeader>
            <DialogTitle className="text-base text-slate-900">Email {ids.length} candidate(s)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <SelectField
              label="Template"
              value={email.template_key}
              onChange={(k) => {
                const t = templates.find((x) => x.key === k);
                setEmail({ template_key: k, subject: t?.subject || "", body: t?.body || "" });
              }}
              options={templates.map((t) => ({ value: t.key, label: t.name }))}
              testid="bulk-email-template"
            />
            <TextField label="Subject" value={email.subject} onChange={(v) => setEmail((e) => ({ ...e, subject: v }))} testid="bulk-email-subject" />
            <TextAreaField label="Message" value={email.body} onChange={(v) => setEmail((e) => ({ ...e, body: v }))} rows={7} testid="bulk-email-body" />
            <p className="text-[11px] text-slate-400">
              {"{candidate_name}"} and {"{recruiter_name}"} are replaced automatically for each recipient.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmailOpen(false)} className="text-slate-500">Cancel</Button>
            <Button onClick={sendBulk} data-testid="bulk-email-send" className="rounded-full bg-blue-600 px-5 hover:bg-blue-500">
              Send emails
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmReject} onOpenChange={setConfirmReject}>
        <AlertDialogContent className="border-slate-200 bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-900">Reject {ids.length} candidate(s)?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              This moves every application for these candidates to the Rejected stage. The action is logged and
              can be reversed by moving them back on the pipeline board.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-200 bg-transparent text-slate-600">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => run("reject", {})} data-testid="bulk-reject-confirm" className="bg-rose-600 hover:bg-rose-500">
              Reject candidates
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ImportDialog({ open, onOpenChange, onDone }) {
  const [step, setStep] = useState("upload");
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const FIELDS = ["full_name", "email", "phone", "location", "current_company", "current_designation",
    "total_experience_years", "skills", "current_ctc", "expected_ctc", "notice_period", "source"];

  const upload = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/candidates/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      const auto = {};
      FIELDS.forEach((f) => {
        const hit = (data.columns || []).find(
          (c) => c.toLowerCase().replace(/[\s_]/g, "") === f.replace(/_/g, "")
        );
        if (hit) auto[f] = hit;
      });
      setMapping(auto);
      setStep("map");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const commit = async () => {
    try {
      const { data } = await api.post("/candidates/import/commit", {
        mapping, rows: preview.rows, skip_duplicates: true,
      });
      toast.success(`Imported ${data.created} candidates · ${data.skipped} duplicates skipped`);
      onOpenChange(false);
      setStep("upload");
      setPreview(null);
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-slate-200 bg-white thin-scroll">
        <DialogHeader>
          <DialogTitle className="text-base text-slate-900">Import candidates from CSV</DialogTitle>
        </DialogHeader>
        {step === "upload" ? (
          <label className="flex cursor-pointer flex-col items-center gap-3 rounded-md border border-dashed border-slate-200 bg-slate-50 py-12">
            <Upload className="h-6 w-6 text-slate-500" />
            <span className="text-sm text-slate-600">Select a CSV file</span>
            <span className="text-xs text-slate-400">Column mapping and duplicate detection happen next</span>
            <input type="file" accept=".csv" className="hidden" data-testid="import-file-input" onChange={(e) => upload(e.target.files?.[0])} />
          </label>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-slate-500">
              {preview.total} rows found. Map your columns to candidate fields — duplicates by email are skipped.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {FIELDS.map((f) => (
                <SelectField
                  key={f}
                  label={f.replace(/_/g, " ")}
                  value={mapping[f] || undefined}
                  onChange={(v) => setMapping((m) => ({ ...m, [f]: v }))}
                  options={preview.columns}
                  placeholder="Not mapped"
                />
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-500">Cancel</Button>
          {step === "map" && (
            <Button onClick={commit} data-testid="import-commit-button" className="rounded-full bg-blue-600 px-5 hover:bg-blue-500">
              Import {preview.total} rows
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CandidateTable({ items, selected, setSelected, onOpen }) {
  const allChecked = items.length > 0 && selected.length === items.length;
  return (
    <div className="surface overflow-x-auto rounded-md thin-scroll">
      <Table>
        <TableHeader>
          <TableRow className="border-slate-200 hover:bg-transparent">
            <TableHead className="w-10">
              <Checkbox
                checked={allChecked}
                onCheckedChange={(v) => setSelected(v ? items.map((i) => i.id) : [])}
                data-testid="select-all-checkbox"
                className="border-slate-300"
              />
            </TableHead>
            {["Candidate", "Experience", "Location", "Skills", "Expected CTC", "Notice", "Source", "Recruiter", "Status"].map((h) => (
              <TableHead key={h} className="label-xs whitespace-nowrap">{h}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((c) => (
            <TableRow key={c.id} data-testid="candidate-row" className="border-slate-200 odd:bg-slate-50/80 hover:bg-slate-100">
              <TableCell>
                <Checkbox
                  checked={selected.includes(c.id)}
                  onCheckedChange={(v) => setSelected((s) => (v ? [...s, c.id] : s.filter((x) => x !== c.id)))}
                  data-testid="candidate-checkbox"
                  className="border-slate-300"
                />
              </TableCell>
              <TableCell className="min-w-[220px]">
                <button onClick={() => onOpen(c)} className="flex items-center gap-3 text-left" data-testid="candidate-name-link">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-blue-100 text-[11px] text-blue-700">
                    {initials(c.full_name)}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-slate-900 hover:text-blue-700">{c.full_name}</span>
                    <span className="block truncate text-[11px] text-slate-500">
                      {c.current_designation || "Role not available"} · {c.current_company || "Company not available"}
                    </span>
                  </span>
                </button>
              </TableCell>
              <TableCell className="font-mono-num whitespace-nowrap text-xs text-slate-600">
                {c.total_experience_years ?? "—"} yrs
              </TableCell>
              <TableCell className="whitespace-nowrap text-xs text-slate-500">
                {c.location?.split(",")[0] || "—"}
              </TableCell>
              <TableCell className="min-w-[180px]">
                <div className="flex flex-wrap gap-1">
                  {(c.skills || []).slice(0, 3).map((s) => (
                    <span key={s} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">{s}</span>
                  ))}
                  {(c.skills || []).length > 3 && (
                    <span className="text-[10px] text-slate-400">+{c.skills.length - 3}</span>
                  )}
                </div>
              </TableCell>
              <TableCell className="font-mono-num whitespace-nowrap text-xs text-slate-500">{c.expected_ctc || "—"}</TableCell>
              <TableCell className="whitespace-nowrap text-xs text-slate-500">{c.notice_period || "—"}</TableCell>
              <TableCell className="whitespace-nowrap text-xs text-slate-500">{c.source || "—"}</TableCell>
              <TableCell className="whitespace-nowrap text-xs text-slate-500">{c.recruiter_name || "—"}</TableCell>
              <TableCell><StatusBadge value={c.status} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function Candidates({ talentPool = false }) {
  const { meta, can } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const [showFilters, setShowFilters] = useState(talentPool);
  const [addOpen, setAddOpen] = useState(false);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [savedSearches, setSavedSearches] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [facets, setFacets] = useState(null);
  const [search, setSearch] = useState(params.get("search") || "");
  const [filters, setFilters] = useState({
    status: "all", location: "all", industry: "all", source: "all", recruiter_id: "all",
    skills: params.get("skills") || "", tags: "", min_experience: null, max_experience: null,
    education: "",
  });

  const load = useCallback(async () => {
    setData(null);
    try {
      const clean = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== "" && v !== null && v !== "all")
      );
      const { data: res } = await api.get("/candidates", {
        params: { search: search || undefined, ...clean, in_talent_pool: talentPool || undefined, page, limit: PAGE_SIZE },
      });
      setData(res);
    } catch (e) {
      toast.error(apiError(e));
      setData({ items: [], total: 0 });
    }
  }, [search, filters, page, talentPool]);

  useEffect(() => {
    const t = setTimeout(load, 260);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/candidates/facets").then((r) => setFacets(r.data)).catch(() => {});
    api.get("/candidates/saved-searches/all").then((r) => setSavedSearches(r.data)).catch(() => {});
  }, []);

  const setFilter = (k) => (v) => {
    setPage(1);
    setFilters((f) => ({ ...f, [k]: v }));
  };

  const saveSearch = async () => {
    if (!searchName.trim()) return toast.error("Give this search a name");
    try {
      const { data: s } = await api.post("/candidates/saved-searches", {
        name: searchName, filters: { ...filters, search },
      });
      setSavedSearches((x) => [s, ...x]);
      setSaveOpen(false);
      setSearchName("");
      toast.success("Search saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const applySaved = (s) => {
    setSearch(s.filters.search || "");
    setFilters((f) => ({ ...f, ...s.filters, search: undefined }));
    setPage(1);
    toast.success(`Applied "${s.name}"`);
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-6">
      <PageHeader
        meta={talentPool ? "Talent CRM" : "Talent database"}
        title={talentPool ? "Talent Pool" : "Candidates"}
        subtitle={
          talentPool
            ? "Long-term searchable talent. Try a natural query like “AUTOSAR + Embedded + 5 years + Bangalore”."
            : "Every candidate record with advanced filters, bulk actions and resume intelligence."
        }
        actions={
          can("candidates", "create") && (
            <>
              <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-candidates-button"
                className="h-9 gap-2 rounded-full border-slate-200 bg-transparent text-xs text-slate-600 hover:bg-slate-100">
                <Upload className="h-3.5 w-3.5" /> Import
              </Button>
              <Button variant="outline" onClick={() => window.open(`${API}/candidates/export/csv`, "_blank")} data-testid="export-candidates-button"
                className="h-9 gap-2 rounded-full border-slate-200 bg-transparent text-xs text-slate-600 hover:bg-slate-100">
                <Download className="h-3.5 w-3.5" /> Export
              </Button>
              <Button variant="outline" onClick={() => setResumeOpen(true)} data-testid="upload-resume-button"
                className="h-9 gap-2 rounded-full border-violet-200 bg-violet-50 text-xs text-violet-700 hover:bg-violet-100">
                <FileUp className="h-3.5 w-3.5" /> Upload resume
              </Button>
              <Button onClick={() => setAddOpen(true)} data-testid="add-candidate-button" className="h-9 gap-2 rounded-full bg-blue-600 px-4 text-xs hover:bg-blue-500">
                <Plus className="h-3.5 w-3.5" /> Add candidate
              </Button>
            </>
          )
        }
      />

      <div className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
            <Input
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              data-testid="candidates-search-input"
              placeholder="Java + Spring Boot + 5 years + Bangalore"
              className="h-9 border-slate-200 bg-slate-50 pl-9 text-sm text-slate-800 placeholder:text-slate-400"
            />
          </div>
          <Button variant="outline" onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters-button"
            className="h-9 gap-2 rounded-full border-slate-200 bg-transparent text-xs text-slate-600 hover:bg-slate-100">
            <SlidersHorizontal className="h-3.5 w-3.5" /> {showFilters ? "Hide" : "Advanced"} filters
          </Button>
          <Button variant="outline" onClick={() => setSaveOpen(true)} data-testid="save-search-button"
            className="h-9 gap-2 rounded-full border-slate-200 bg-transparent text-xs text-slate-600 hover:bg-slate-100">
            <Bookmark className="h-3.5 w-3.5" /> Save search
          </Button>
        </div>

        {savedSearches.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="label-xs">Saved</span>
            {savedSearches.map((s) => (
              <button
                key={s.id}
                onClick={() => applySaved(s)}
                data-testid="saved-search-chip"
                className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] text-slate-600 hover:border-blue-300"
              >
                {s.name}
              </button>
            ))}
          </div>
        )}

        {showFilters && (
          <div className="surface grid gap-3 rounded-md p-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="advanced-filters">
            <SelectField label="Status" value={filters.status} onChange={setFilter("status")} allowAll options={facets?.statuses || []} testid="filter-status" />
            <SelectField label="Location" value={filters.location} onChange={setFilter("location")} allowAll options={facets?.locations || []} testid="filter-location" />
            <SelectField label="Industry" value={filters.industry} onChange={setFilter("industry")} allowAll options={facets?.industries || []} testid="filter-industry" />
            <SelectField label="Source" value={filters.source} onChange={setFilter("source")} allowAll options={facets?.sources || []} testid="filter-source" />
            <SelectField label="Recruiter" value={filters.recruiter_id} onChange={setFilter("recruiter_id")} allowAll
              options={(meta?.users || []).map((u) => ({ value: u.id, label: u.name }))} testid="filter-recruiter" />
            <TextField label="Skills (comma separated)" value={filters.skills} onChange={setFilter("skills")} testid="filter-skills" placeholder="AUTOSAR, CAN" />
            <TextField label="Min experience" type="number" value={filters.min_experience} onChange={setFilter("min_experience")} testid="filter-min-exp" />
            <TextField label="Max experience" type="number" value={filters.max_experience} onChange={setFilter("max_experience")} testid="filter-max-exp" />
            <TextField label="Tags (comma separated)" value={filters.tags} onChange={setFilter("tags")} placeholder="Hot, Referral" />
            <TextField label="Education contains" value={filters.education} onChange={setFilter("education")} placeholder="M.Tech" />
            <div className="flex items-end">
              <Button variant="ghost" data-testid="clear-filters-button"
                onClick={() => {
                  setFilters({ status: "all", location: "all", industry: "all", source: "all", recruiter_id: "all", skills: "", tags: "", min_experience: null, max_experience: null, education: "" });
                  setSearch("");
                }}
                className="h-9 text-xs text-slate-500">
                Clear all filters
              </Button>
            </div>
          </div>
        )}
      </div>

      {selected.length > 0 && can("candidates", "edit") && (
        <BulkBar ids={selected} clear={() => setSelected([])} onDone={() => { setSelected([]); load(); }} />
      )}

      {!data ? (
        <TableSkeleton rows={8} cols={6} />
      ) : data.items.length === 0 ? (
        <EmptyState
          icon={Users}
          testid="candidates-empty-state"
          title="No candidates match this search"
          description="Broaden your filters, or add a candidate by uploading a resume."
          action={can("candidates", "create") && (
            <Button onClick={() => setResumeOpen(true)} className="rounded-full bg-blue-600 hover:bg-blue-500">
              Upload a resume
            </Button>
          )}
        />
      ) : (
        <>
          <CandidateTable items={data.items} selected={selected} setSelected={setSelected} onOpen={(c) => navigate(`/candidates/${c.id}`)} />
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-400">
              Page {page} of {totalPages} · {data.total} records
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}
                data-testid="prev-page-button" className="h-8 rounded-full border-slate-200 bg-transparent text-xs text-slate-600">
                Previous
              </Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
                data-testid="next-page-button" className="h-8 rounded-full border-slate-200 bg-transparent text-xs text-slate-600">
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-md border-slate-200 bg-white">
          <DialogHeader>
            <DialogTitle className="text-base text-slate-900">Save this search</DialogTitle>
          </DialogHeader>
          <TextField label="Name" value={searchName} onChange={setSearchName} testid="save-search-name" placeholder="AUTOSAR 5-8 yrs Bangalore" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveOpen(false)} className="text-slate-500">Cancel</Button>
            <Button onClick={saveSearch} data-testid="save-search-confirm" className="rounded-full bg-blue-600 px-5 hover:bg-blue-500">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CandidateForm open={addOpen} onOpenChange={setAddOpen} onSaved={() => load()} />
      <ResumeUpload open={resumeOpen} onOpenChange={setResumeOpen} onSaved={(c) => navigate(`/candidates/${c.id}`)} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />
    </div>
  );
}
